import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, IconButton } from "@mui/material";
import { RestartAlt, Image as ImageIcon, Close } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { palette } from "../../theme";
import { ChatInput } from "../../components/chat/chat-input";
import { MessagesList } from "../../components/chat/messages-list";
import { REACT_APP_STREAM_CHAT_ENDPOINT } from "../../base-env";
import { ASSETS } from "../../consts";
import { 
  useGetStatsCoursesByCourseIdTopicsQuery,
  usePostActivitiesAiTeacherMutation
} from "../../store/api/umbraco-api";
import { userSelector } from "../../store/session/selectors";
import { useCourses } from "../../context/courses-context";
import { 
  generatePseudoUUID, 
  startChatTracking, 
  incrementMessageCount, 
  endChatTracking 
} from "../../services/ai-teacher-tracking";

interface Message {
  sender: string;
  text: string;
  imageUrls?: string[];
}

interface ImageFile {
  file: File;
  preview: string;
}

const GeneralChat: React.FC = () => {
  const { courseId } = useParams<{ courseId?: string }>();
  const [input, setInput] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastResponseId, setLastResponseId] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<ImageFile[]>([]);
  const [messages, setMessages] = useState<Array<Message>>([
    { sender: "AI", text: `היי! כאן TAUGA AI, הבינה המלאכותית. במה אוכל לעזור לך היום?` },
  ]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sessionStartTime, setSessionStartTime] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Use a ref to hold all tracking-related data to avoid stale closures in event listeners
  const trackingStateRef = useRef({
    conversationId: null as string | null,
    startTime: null as Date | null,
    userMessageCount: 0,
  });

  // AI Teacher activity tracking
  const [postAiTeacherActivity] = usePostActivitiesAiTeacherMutation();

  // Fetch course topics for context if courseId is available
  const { data: courseTopics } = useGetStatsCoursesByCourseIdTopicsQuery(
    { courseId: courseId ? Number(courseId) : 0 },
    { skip: !courseId }
  );

  const user = useSelector(userSelector);
  const { selectedCourseData } = useCourses();

  const endCurrentSession = useCallback(() => {
    if (trackingStateRef.current.startTime && trackingStateRef.current.conversationId) {
      endChatTracking({
        conversationId: trackingStateRef.current.conversationId,
        userMessageCount: trackingStateRef.current.userMessageCount,
        startTime: trackingStateRef.current.startTime,
      });
      // Mark session as ended
      trackingStateRef.current.startTime = null;
    }
  }, []);

  const startNewSession = useCallback(() => {
    // End any existing session before starting a new one
    endCurrentSession();

    const newConversationId = generatePseudoUUID();
    const trackingStartTime = new Date();

    trackingStateRef.current = {
      conversationId: newConversationId,
      startTime: trackingStartTime,
      userMessageCount: 0,
    };

    startChatTracking({
      conversationId: newConversationId,
      courseId: selectedCourseData?.id || null,
      courseName: selectedCourseData?.name || 'N/A',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Guest',
      userGroups: [], // user object does not contain groups
      startTime: trackingStartTime.toISOString(),
    });
  }, [selectedCourseData, user, endCurrentSession]);

  // Cleanup function to post activity end when component unmounts
  useEffect(() => {
    return () => {
      if (sessionStartTime && hasInteracted) {
        postAiTeacherActivity({
          aiTeacherActivityRequest: {
            startTime: sessionStartTime,
            endTime: new Date().toISOString()
          }
        }).catch(() => {
          // Silently ignore activity end errors
        });
      }
    };
  }, [sessionStartTime, hasInteracted, postAiTeacherActivity]);

  useEffect(() => {
    // Start the first session when the component mounts
    startNewSession();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched away from the tab
        endCurrentSession();
      } else {
        // User came back to the tab
        startNewSession();
      }
    };

    const handleBeforeUnload = () => endCurrentSession();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on component unmount
    return () => {
      endCurrentSession();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [startNewSession, endCurrentSession]);

  const handleStream = async (message: string, imageFiles?: File[]) => {
    try {
      let response;
      
      if (imageFiles && imageFiles.length > 0) {
        const formData = new FormData();
        imageFiles.forEach((file, index) => {
          formData.append(`image${index}`, file);
        });
        if (message) {
          formData.append('message', message);
        }
        // Add course topics context if available
        if (courseTopics) {
          formData.append('courseTopics', JSON.stringify(courseTopics));
        }

        response = await fetch(REACT_APP_STREAM_CHAT_ENDPOINT, {
          method: 'POST',
          body: formData,
        });
      } else {
        const requestBody: any = { 
          message,
          previous_response_id: lastResponseId 
        };
        
        // Add course topics context if available
        if (courseTopics) {
          requestBody.courseTopics = courseTopics;
        }

        response = await fetch(REACT_APP_STREAM_CHAT_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader available');

      let fullMessage = '';
      setIsStreaming(true);
      setStreamingMessage("");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const event = JSON.parse(data);
              if (event.text) {
                fullMessage += event.text;
                setStreamingMessage(fullMessage);
              } else if (event.done && event.response_id) {
                // Debug log removed
                
                setLastResponseId(event.response_id);
                setMessages((prev) => [...prev, { sender: "AI", text: fullMessage }]);
                setStreamingMessage("");
                setIsStreaming(false);
                setIsBotTyping(false);
                return;
              } else if (event.error) {
                throw new Error(event.error);
              }
            } catch (e) {
              if (data.startsWith('{')) {
                console.error('Error parsing event:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      setIsBotTyping(false);
      setIsStreaming(false);
      setStreamingMessage("");
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() && selectedImages.length === 0) return;

    if (trackingStateRef.current.conversationId) {
      incrementMessageCount(trackingStateRef.current.conversationId);
      trackingStateRef.current.userMessageCount++;
    }
    
    // Track AI Teacher activity on first interaction (just store start time)
    if (!hasInteracted) {
      const startTime = new Date().toISOString();
      setSessionStartTime(startTime);
      setHasInteracted(true);
    }
    
    // Debug log removed
    if (selectedImages.length > 0) {
      // Debug log removed
      
      const userMessage = { 
        sender: "User", 
        text: input, 
        imageUrls: selectedImages.map(img => img.preview) 
      };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setSelectedImages([]);
      setIsBotTyping(true);
      await handleStream(input, selectedImages.map(img => img.file));
    } else {
      // Debug log removed
      
      const userMessage = { sender: "User", text: input };
      setMessages((prev) => [...prev, userMessage]);
      setInput("");
      setIsBotTyping(true);
      await handleStream(input);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    
    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const handleRemoveImage = (index: number) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleStartOver = () => {
    // Reset session state for new conversation
    setSessionStartTime(null);
    setHasInteracted(false);

    setMessages([{ sender: "AI", text: `היי! כאן TAUGA AI, הבינה המלאכותית. במה אוכל לעזור לך היום?` }]);
    setLastResponseId(null);
    setIsBotTyping(false);
    selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  return (
    <Box sx={styles.container}>
      {/* Header */}
      <Box sx={styles.header}>
        <Box sx={styles.headerContent}>
          <Box sx={styles.avatarContainer}>
            <img
              src={ASSETS.AI_CHAT_IMAGE}
              alt="TAUGA AI"
              style={styles.avatarImage}
            />
            <Box sx={styles.onlineIndicator} />
          </Box>
          <Box>
            <Typography sx={styles.headerTitle}>
              TAUGA AI
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleStartOver}
          sx={styles.restartButton}
        >
          <RestartAlt />
        </IconButton>
      </Box>

      {/* Messages area */}
      <Box sx={styles.messagesContainer}>
        <MessagesList
          messages={messages}
          isBotTyping={isBotTyping}
          streamingMessage={streamingMessage}
          isStreaming={isStreaming}
        />
      </Box>

      {/* Chat input */}
      <Box sx={styles.inputContainer}>
        {selectedImages.length > 0 && (
          <Box sx={styles.imagesPreviewContainer}>
            {selectedImages.map((image, index) => (
              <Box key={index} sx={styles.imagePreviewContainer}>
                <img
                  src={image.preview}
                  alt={`Selected ${index + 1}`}
                  style={styles.imagePreview}
                />
                <IconButton
                  onClick={() => handleRemoveImage(index)}
                  sx={styles.removeImageButton}
                >
                  <Close />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={styles.inputWrapper}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            ref={fileInputRef}
            multiple
          />
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            sx={styles.imageButton}
          >
            <ImageIcon />
          </IconButton>
          <ChatInput
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
          />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: palette.white,
    borderRadius: "24px",
    borderWidth: 1,
    borderColor: palette.gray_light,
    borderStyle: "solid",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%",
    maxWidth: 800,
    margin: "0 auto",
  },
  header: {
    padding: "20px",
    borderBottom: `1px solid ${palette.gray_light}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: palette.white,
    backdropFilter: "blur(8px)",
    flexShrink: 0,
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  avatarContainer: {
    position: "relative",
    width: 48,
    height: 48,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    objectFit: "cover" as const,
    border: `2px solid ${palette.purple_light}`,
  },
  onlineIndicator: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    border: "2px solid white",
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
    color: palette.gray,
    marginBottom: "2px",
  },
  headerSubtitle: {
    color: palette.gray,
    fontSize: "0.85rem",
    opacity: 0.8,
  },
  restartButton: {
    color: palette.purple,
    backgroundColor: palette.purple_light,
    opacity: 0.8,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: palette.purple_light,
      opacity: 1,
      transform: "scale(1.05)",
    },
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
    backgroundColor: palette.gray_extra_light,
    minHeight: 0,
    "&::-webkit-scrollbar": {
      width: "8px",
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: palette.gray_light,
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: palette.gray,
      },
    },
  },
  inputContainer: {
    borderTop: `1px solid ${palette.gray_light}`,
    padding: "20px",
    backgroundColor: palette.white,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flexShrink: 0,
  },
  imagesPreviewContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '8px',
  },
  imagePreviewContainer: {
    position: "relative",
    width: "100px",
    height: "100px",
    borderRadius: "8px",
    overflow: "hidden",
  },
  imagePreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  removeImageButton: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  imageButton: {
    color: palette.purple,
    backgroundColor: palette.purple_light,
    opacity: 0.8,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: palette.purple_light,
      opacity: 1,
      transform: "scale(1.05)",
    },
  },
};

export default GeneralChat; 